import {
    ASSET_CLASS_IDS,
    MEASURE_SLUGS,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import { Filter } from "~/modules/filters/types";
import { ColumnSpanConfig } from "~/types";
import { TokenTimeseriesGroupBy } from "~/modules/token_timeseries/types";
import { AssetTimeseriesGroupBy } from "~/modules/asset_timeseries_v4/types";
import { getAllTokensStackedChart } from "~/modules/token_timeseries/shared/getAllTokensStackedChart";
import { getMintBurnVolumeChart } from "~/modules/token_timeseries/shared/getMintBurnVolumeChart";
import { getHoldersChart } from "~/modules/token_timeseries/shared/getHoldersChart";
import { getActiveAddressesChart } from "~/modules/token_timeseries/shared/getActiveAddressesChart";
import { getTransferVolumeChart } from "~/modules/token_timeseries/shared/getTransferVolumeChart";
import { getTransferCountChart } from "~/modules/token_timeseries/shared/getTransferCountChart";
import { getNetAssetValueChart } from "~/modules/asset_timeseries_v4/shared/getNetAssetValueChart";
import { getPriceChart } from "~/modules/asset_timeseries_v4/shared/getPriceChart";
import { VisualizationMode } from "~/modules/visualization/components/Timeseries/types";
import { VisualizationModeCard } from "~/modules/visualization/components/Timeseries/VisualizationModeCard";
import { Asset } from "~/modules/assets/types";
import { getValueByAssetClassID } from "~/utils/getValueByAssetClass";
import { getAssetApyConfig } from "../../lib/getAssetApy";
import { getApyChart } from "~/modules/asset_timeseries_v4/shared/getApyChart";

// // // //

interface AssetClassChartConfig {
    key: string;
    title: string;
    measureSlug: string;
    groupByOptions: TokenTimeseriesGroupBy[];
    groupLimit?: number;
    mainChartLabel: string;
    mainChartDescription?: string;
    holdersGroupByOptions: TokenTimeseriesGroupBy[];
    holdersDefaultGroupBy?: TokenTimeseriesGroupBy;
    includeNAV: boolean;
    includePrice: boolean;
    includeMintBurn: boolean;
    includeActiveAddresses: boolean;
    includeTransferVolume: boolean;
    includeTransferCount: boolean;
    includeAPY: "always" | "conditional" | "never";
    navYAxisMinUndefined?: boolean;
    activeAddressesGroupByOptions?: TokenTimeseriesGroupBy[];
    transferVolumeGroupByOptions?: TokenTimeseriesGroupBy[];
    transferCountGroupByOptions?: TokenTimeseriesGroupBy[];
    mintBurnDescription?: string;
}

function getChartConfig(asset: Asset): AssetClassChartConfig {
    const DEFAULT_GROUP_BY_OPTIONS = [
        TokenTimeseriesGroupBy.asset,
        TokenTimeseriesGroupBy.token,
        TokenTimeseriesGroupBy.network,
        TokenTimeseriesGroupBy.protocol,
        TokenTimeseriesGroupBy.manager,
        TokenTimeseriesGroupBy.jurisdictionCountry,
    ];

    return getValueByAssetClassID<AssetClassChartConfig>({
        assetClassID: asset.asset_class_id,
        values: {
            treasury: {
                key: "treasury",
                title: "Treasury Fund Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                mainChartLabel: "Total Value",
                groupByOptions: DEFAULT_GROUP_BY_OPTIONS,
                holdersGroupByOptions: DEFAULT_GROUP_BY_OPTIONS,
                includeNAV: true,
                includePrice: false,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "conditional",
            },
            non_us_government_debt: {
                key: "non-us-government-debt",
                title: "Non-U.S. Government Debt Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                mainChartLabel: "Total Value",
                groupByOptions: DEFAULT_GROUP_BY_OPTIONS,
                holdersGroupByOptions: DEFAULT_GROUP_BY_OPTIONS,
                includeNAV: true,
                includePrice: false,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "conditional",
                navYAxisMinUndefined: true,
            },
            stablecoin: {
                key: "stablecoins",
                title: "Stablecoins Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_market_cap_dollar,
                mainChartLabel: "Market Cap",
                mintBurnDescription:
                    "View periods where issuers are minting new stablecoins and holders are requesting redemptions.",
                groupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.peggedAsset,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                holdersGroupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.peggedAsset,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                includeNAV: false,
                includePrice: true,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: true,
                includeAPY: "never",
                activeAddressesGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.peggedAsset,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                transferVolumeGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.peggedAsset,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                transferCountGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.peggedAsset,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
            },
            commodity: {
                key: "commodities",
                title: "Commodity Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                mainChartLabel: "Total Value",
                groupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                ],
                holdersGroupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.peggedAsset,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                includeNAV: true,
                includePrice: true,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "never",
                navYAxisMinUndefined: true,
                activeAddressesGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.protocol,
                ],
                transferVolumeGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.peggedAsset,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
            },
            real_estate: {
                key: "real-estate",
                title: "Real Estate Metrics",
                measureSlug: MEASURE_SLUGS.total_asset_value_dollar,
                mainChartLabel: "Total Value",
                groupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.issuer,
                    TokenTimeseriesGroupBy.protocol,
                ],
                holdersGroupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.issuer,
                    TokenTimeseriesGroupBy.protocol,
                ],
                includeNAV: true,
                includePrice: false,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "never",
            },
            stocks: {
                key: "public-equity",
                title: "Tokenized Stock Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                mainChartLabel: "Total Value",
                groupLimit: 25,
                groupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                holdersGroupByOptions: [
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                holdersDefaultGroupBy: TokenTimeseriesGroupBy.protocol,
                includeNAV: true,
                includePrice: false,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "never",
                navYAxisMinUndefined: true,
                activeAddressesGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                transferVolumeGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
            },
            private_equity: {
                key: "public-equity",
                title: "Tokenized Stock Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                mainChartLabel: "Total Value",
                groupLimit: 25,
                groupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                holdersGroupByOptions: [
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                holdersDefaultGroupBy: TokenTimeseriesGroupBy.protocol,
                includeNAV: true,
                includePrice: false,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "never",
                navYAxisMinUndefined: true,
                activeAddressesGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                transferVolumeGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
            },
            venture_capital: {
                key: "public-equity",
                title: "Tokenized Stock Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                mainChartLabel: "Total Value",
                groupLimit: 25,
                groupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                holdersGroupByOptions: [
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                holdersDefaultGroupBy: TokenTimeseriesGroupBy.protocol,
                includeNAV: true,
                includePrice: false,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "never",
                navYAxisMinUndefined: true,
                activeAddressesGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
                transferVolumeGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.jurisdictionCountry,
                ],
            },
            municipal_bonds: {
                key: "municipal-bonds",
                title: "Credit Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                mainChartLabel: "Total Value",
                groupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.issuer,
                    TokenTimeseriesGroupBy.manager,
                ],
                holdersGroupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.issuer,
                    TokenTimeseriesGroupBy.manager,
                ],
                includeNAV: true,
                includePrice: false,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "conditional",
                activeAddressesGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.protocol,
                ],
                transferVolumeGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.protocol,
                ],
            },
            corporate_credit: {
                key: "corporate-credit",
                title: "Credit Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                mainChartLabel: "Total Value",
                groupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.issuer,
                    TokenTimeseriesGroupBy.manager,
                ],
                holdersGroupByOptions: [
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.protocol,
                    TokenTimeseriesGroupBy.issuer,
                    TokenTimeseriesGroupBy.manager,
                ],
                includeNAV: true,
                includePrice: false,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "conditional",
                activeAddressesGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.protocol,
                ],
                transferVolumeGroupByOptions: [
                    TokenTimeseriesGroupBy.measure,
                    TokenTimeseriesGroupBy.asset,
                    TokenTimeseriesGroupBy.token,
                    TokenTimeseriesGroupBy.network,
                    TokenTimeseriesGroupBy.protocol,
                ],
            },
            diversified_credit: {
                key: "diversified-credit",
                title: "Credit Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                mainChartLabel: "Total Value",
                groupByOptions: DEFAULT_GROUP_BY_OPTIONS,
                holdersGroupByOptions: DEFAULT_GROUP_BY_OPTIONS,
                includeNAV: true,
                includePrice: false,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "conditional",
            },
            asset_backed_credit: {
                key: "asset-backed-credit",
                title: "Credit Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                mainChartLabel: "Total Value",
                groupByOptions: DEFAULT_GROUP_BY_OPTIONS,
                holdersGroupByOptions: DEFAULT_GROUP_BY_OPTIONS,
                includeNAV: true,
                includePrice: false,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "conditional",
            },
            specialty_finance: {
                key: "specialty-finance",
                title: "Credit Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                mainChartLabel: "Total Value",
                groupByOptions: DEFAULT_GROUP_BY_OPTIONS,
                holdersGroupByOptions: DEFAULT_GROUP_BY_OPTIONS,
                includeNAV: true,
                includePrice: false,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "conditional",
            },
            active_strategies: {
                key: "actively-managed-strategies",
                title: "Asset Metrics",
                measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
                mainChartLabel: "Total Value",
                groupByOptions: DEFAULT_GROUP_BY_OPTIONS,
                holdersGroupByOptions: DEFAULT_GROUP_BY_OPTIONS,
                includeNAV: true,
                includePrice: false,
                includeMintBurn: true,
                includeActiveAddresses: true,
                includeTransferVolume: true,
                includeTransferCount: false,
                includeAPY: "conditional",
            },
        },
        fallbackValue: {
            key: "asset",
            title: "Asset Metrics",
            measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
            mainChartLabel: "Total Value",
            groupByOptions: DEFAULT_GROUP_BY_OPTIONS,
            holdersGroupByOptions: DEFAULT_GROUP_BY_OPTIONS,
            includeNAV: false,
            includePrice: false,
            includeMintBurn: true,
            includeActiveAddresses: false,
            includeTransferVolume: false,
            includeTransferCount: false,
            includeAPY: "never",
        },
    });
}

function buildModes(props: {
    asset: Asset;
    config: AssetClassChartConfig;
    filters: Filter[];
    assetTimeSeriesFilters: Filter[];
    defaultGroupBy: TokenTimeseriesGroupBy;
    enableGroupByControls: boolean;
    includeApyChart: boolean;
}): VisualizationMode[] {
    const {
        asset,
        config,
        filters,
        assetTimeSeriesFilters,
        defaultGroupBy,
        enableGroupByControls,
        includeApyChart,
    } = props;

    const isStablecoin = asset.asset_class_id === ASSET_CLASS_IDS.Stablecoin;
    const isDistributed =
        asset.tokenization_type_ids?.some(
            (tokenizationTypeID) =>
                Number(tokenizationTypeID) ===
                TOKENIZATION_TYPE_IDS.OnChainDistributed,
        ) ?? false;

    // Define key prefix for chart filenames
    let prefixKey = config.key;
    if (asset.ticker) {
        // Remove all characters that are not alphanumeric
        const sanitizedTicker = asset.ticker
            .replace(/[^a-zA-Z0-9]/g, "")
            .toLowerCase();
        prefixKey = sanitizedTicker;
    }

    // Main chart (market cap / total value)
    const mainChart = getAllTokensStackedChart({
        key: `${prefixKey}-market-caps`,
        label: config.mainChartLabel,
        description: config.mainChartDescription,
        filters,
        defaultGroupBy,
        enableGroupByControls,
        measureSlug: config.measureSlug,
        ignoreDownloadsFeatureFlag: true,
        groupByOptions: config.groupByOptions,
        groupLimit: config.groupLimit,
    });

    // NAV chart (individual only)
    const navChart = config.includeNAV
        ? (() => {
              const navBase = getNetAssetValueChart({
                  key: `${prefixKey}-net-asset-value`,
                  label: "NAV",
                  filters: assetTimeSeriesFilters,
                  enableGroupByControls,
                  ignoreDownloadsFeatureFlag: true,
                  ...(config.key === "commodities"
                      ? { defaultGroupBy: AssetTimeseriesGroupBy.asset }
                      : {}),
              });

              const nav = config.navYAxisMinUndefined
                  ? {
                        ...navBase,
                        visualizationConfig: {
                            ...navBase.visualizationConfig,
                            yAxis: { min: undefined },
                        },
                    }
                  : navBase;

              return nav;
          })()
        : null;

    // Transfer Volume
    const transferVolumeChart =
        config.includeTransferVolume && (isStablecoin || isDistributed)
            ? getTransferVolumeChart({
                  key: `${prefixKey}-transfer-volume`,
                  filters,
                  defaultGroupBy: TokenTimeseriesGroupBy.network,
                  enableGroupByControls,
                  groupByOptions: config.transferVolumeGroupByOptions ?? [],
              })
            : null;

    // Active Addresses
    const activeAddressesChart =
        config.includeActiveAddresses && (isStablecoin || isDistributed)
            ? getActiveAddressesChart({
                  key: `${prefixKey}-active-addresses`,
                  filters,
                  defaultGroupBy: TokenTimeseriesGroupBy.network,
                  enableGroupByControls,
                  groupByOptions: config.activeAddressesGroupByOptions ?? [],
              })
            : null;

    // Price chart (individual only)
    const priceChart = config.includePrice
        ? getPriceChart({
              key: `${prefixKey}-price`,
              label: "Price",
              filters: assetTimeSeriesFilters,
              enableGroupByControls: false,
          })
        : null;

    // Mint/Burn Volume
    const mintBurnChart = config.includeMintBurn
        ? getMintBurnVolumeChart({
              key: `${prefixKey}-fund-flows`,
              description: config.mintBurnDescription,
              filters,
          })
        : null;

    // Transfer Count (stablecoins group view only)
    const transferCountChart =
        config.includeTransferCount && (isStablecoin || isDistributed)
            ? getTransferCountChart({
                  key: `${prefixKey}-transfer-count`,
                  filters,
                  defaultGroupBy: TokenTimeseriesGroupBy.network,
                  enableGroupByControls,
                  groupByOptions: config.transferCountGroupByOptions ?? [],
              })
            : null;

    // Holders chart
    const holdersChart =
        config.holdersGroupByOptions.length > 0
            ? {
                  ...getHoldersChart({
                      key: `${prefixKey}-holders`,
                      filters,
                      defaultGroupBy:
                          config.holdersDefaultGroupBy ?? defaultGroupBy,
                      enableGroupByControls:
                          config.key === "treasury"
                              ? false
                              : enableGroupByControls,
                      groupByOptions: config.holdersGroupByOptions,
                  }),
                  content: {
                      label: "Holders",
                  },
              }
            : null;

    // APY chart
    const apyChartConfig = getAssetApyConfig(asset); // Get apyChartConfig
    const apyChart =
        config.includeAPY === "always" ||
        (config.includeAPY === "conditional" && includeApyChart)
            ? getApyChart({
                  key: `${prefixKey}-${apyChartConfig.measureSlug.replaceAll("_", "-")}`,
                  label: apyChartConfig.label,
                  filters,
                  enableGroupByControls: false,
                  measureSlug: apyChartConfig.measureSlug,
              })
            : null;

    const orderedModes = isStablecoin
        ? [
              mainChart,
              priceChart,
              holdersChart,
              mintBurnChart,
              transferVolumeChart,
              transferCountChart,
              activeAddressesChart,
          ]
        : [
              mainChart,
              navChart,
              holdersChart,
              mintBurnChart,
              transferVolumeChart,
              transferCountChart,
              activeAddressesChart,
              apyChart,
              priceChart,
          ];

    return orderedModes
        .filter((mode): mode is VisualizationMode => mode !== null)
        .map((mode, index) => ({
            ...mode,
            loginRequired: index > 0,
        }));
}

// // // //

export function AssetTimeSeriesCard(props: {
    asset: Asset;
    filters: Filter[];
    assetTimeSeriesFilters?: Filter[];
    columnSpanConfig?: ColumnSpanConfig;
    enableGroupByControls: boolean;
    defaultGroupBy: TokenTimeseriesGroupBy;
    includeApyChart?: boolean;
}) {
    const {
        asset,
        filters,
        assetTimeSeriesFilters = [],
        defaultGroupBy,
        enableGroupByControls,
        includeApyChart = false,
        columnSpanConfig = {
            sm: 12,
            md: 12,
            lg: 12,
            xl: 8,
            xxl: 8,
        },
    } = props;

    const config = getChartConfig(asset);

    const title = `${asset.name} Metrics`;

    const modes = buildModes({
        asset,
        config,
        filters,
        assetTimeSeriesFilters,
        defaultGroupBy,
        enableGroupByControls,
        includeApyChart,
    });

    return (
        <VisualizationModeCard
            title={title}
            modes={modes}
            columnSpanConfig={columnSpanConfig}
        />
    );
}
